import React, { useEffect, useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Bar, ComposedChart } from 'recharts';
import { Activity, TrendingUp, Sparkles, Send, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const WINDOWS = [
  { key: '10r', label: 'Last 10 rounds' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'all', label: 'All time' },
];

const correlationTone = (r) => {
  if (r === null || r === undefined) return { text: 'Not enough data', tone: 'text-[#A8A29E]' };
  if (r <= -0.5) return { text: `Strong link · r=${r}`, tone: 'text-[#2C5530]' };
  if (r <= -0.2) return { text: `Moderate link · r=${r}`, tone: 'text-[#57534E]' };
  if (r <= 0.2) return { text: `Weak link · r=${r}`, tone: 'text-[#A8A29E]' };
  return { text: `Reverse pattern · r=${r}`, tone: 'text-[#BC562B]' };
};

const ScoreVsMentalPanel = () => {
  const [window, setWindow] = useState('10r');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  const load = async (w = window) => {
    setLoading(true);
    try {
      const { data } = await API.get(`/locker/score-vs-mental?window=${w}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(window); /* eslint-disable-next-line */ }, [window]);

  const chartData = useMemo(() => (data?.series || []).map((s, i) => ({
    idx: i + 1,
    date: s.round_date || s.id,
    strokes: s.strokes,
    to_par: s.score_to_par,
    mental: s.mental_composite,
  })), [data]);

  const generateBrief = async () => {
    setBriefBusy(true);
    setBrief(null);
    try {
      const { data } = await API.post('/locker/score-vs-mental/insight', { window });
      setBrief(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate brief.');
    } finally {
      setBriefBusy(false);
    }
  };

  const share = async () => {
    if (!brief?.id) return;
    setShareBusy(true);
    try {
      const { data } = await API.post('/locker/score-vs-mental/share', { brief_id: brief.id });
      toast.success(data.message || 'Shared with your coach.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to share.');
    } finally {
      setShareBusy(false);
    }
  };

  const stats = data?.stats || {};
  const corr = correlationTone(stats.correlation_mental_vs_strokes);

  return (
    <div className="bg-white border-t-4 border-[#BC562B] p-5 md:p-6" data-testid="score-vs-mental-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BC562B] mb-1 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5" /> Score vs Mental</p>
          <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A]">Is the mental work moving the score?</h3>
          <p className="text-sm text-[#57534E] mt-1">Only Round Reviews with a score count toward the chart.</p>
        </div>
        <div className="flex flex-wrap gap-1" data-testid="svm-window">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWindow(w.key)}
              className={`text-[11px] uppercase tracking-wider px-3 py-1.5 border ${window === w.key ? 'bg-[#1A2E1D] text-white border-[#1A2E1D]' : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#1A2E1D]/40'}`}
              data-testid={`svm-window-${w.key}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-56 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#BC562B]" /></div>
      ) : stats.rounds_with_score < 2 ? (
        <div className="bg-[#FAFAF9] border border-[#E7E5E4] p-6 text-center" data-testid="svm-empty">
          <Info className="w-6 h-6 text-[#A8A29E] mx-auto mb-2" />
          <p className="text-sm text-[#57534E]">Log at least a couple of Round Reviews with a score to see the pattern here.</p>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="h-64" data-testid="svm-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 40, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F2F0E9" />
                <XAxis dataKey="idx" tick={{ fill: '#A8A29E', fontSize: 10 }} label={{ value: 'Rounds (oldest → newest)', position: 'insideBottom', offset: -2, fill: '#A8A29E', fontSize: 10 }} />
                <YAxis yAxisId="strokes" orientation="left" tick={{ fill: '#57534E', fontSize: 11 }} label={{ value: 'Strokes', angle: -90, position: 'insideLeft', fill: '#57534E', fontSize: 10 }} />
                <YAxis yAxisId="mental" orientation="right" domain={[0, 10]} tick={{ fill: '#BC562B', fontSize: 11 }} label={{ value: 'Mental /10', angle: 90, position: 'insideRight', fill: '#BC562B', fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E7E5E4' }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="strokes" dataKey="strokes" name="Strokes" fill="#1A2E1D" fillOpacity={0.7} />
                <Line yAxisId="mental" type="monotone" dataKey="mental" name="Mental composite" stroke="#BC562B" strokeWidth={2.5} dot={{ fill: '#BC562B', r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
            <div className="bg-[#FAFAF9] p-3 border border-[#E7E5E4]" data-testid="svm-stat-avg-strokes">
              <p className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Avg strokes</p>
              <p className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">{stats.avg_strokes ?? '—'}</p>
              <p className="text-[10px] text-[#57534E]">±{stats.strokes_stdev ?? '—'}</p>
            </div>
            <div className="bg-[#FAFAF9] p-3 border border-[#E7E5E4]" data-testid="svm-stat-avg-mental">
              <p className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Avg mental</p>
              <p className="font-['Playfair_Display'] text-2xl text-[#BC562B]">{stats.avg_mental ?? '—'}</p>
              <p className="text-[10px] text-[#57534E]">/ 10</p>
            </div>
            <div className="bg-[#FAFAF9] p-3 border border-[#E7E5E4] col-span-2" data-testid="svm-stat-corr">
              <p className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Mental ↔ strokes link</p>
              <p className={`text-sm font-semibold ${corr.tone}`}>{corr.text}</p>
              <p className="text-[10px] text-[#57534E] mt-0.5">
                {stats.mental_trend_slope > 0 ? 'Mental trending up' : stats.mental_trend_slope < 0 ? 'Mental trending down' : 'Mental flat'} · {' '}
                {stats.strokes_trend_slope < 0 ? 'strokes trending down' : stats.strokes_trend_slope > 0 ? 'strokes trending up' : 'strokes flat'}
              </p>
            </div>
          </div>

          {/* AI coaching brief */}
          <div className="mt-5 bg-[#1A2E1D] text-white p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#E89B6E]">Coaching brief</p>
                <p className="text-sm text-white/85 max-w-xl mt-1">
                  {brief ? 'Generated from your last rounds. Two or three drills tied to your weakest A³ area.' : 'Generate a short brief with a pattern I can see, plus 2-3 drills tied to your weakest A³ area.'}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={generateBrief}
                  disabled={briefBusy || stats.rounds_with_score < 3}
                  className="inline-flex items-center gap-2 bg-[#BC562B] text-white px-4 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-[#9A4520] disabled:opacity-50"
                  data-testid="svm-generate-brief"
                >
                  {briefBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {brief ? 'Regenerate' : 'Generate brief'}
                </button>
                {brief && (
                  <button
                    onClick={share}
                    disabled={shareBusy}
                    className="inline-flex items-center gap-2 border border-white/40 text-white px-4 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-white/10 disabled:opacity-50"
                    data-testid="svm-share-brief"
                  >
                    {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to my coach
                  </button>
                )}
              </div>
            </div>

            {brief && (
              <div className="mt-5 space-y-4" data-testid="svm-brief">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#E89B6E] mb-1">Pattern spotted</p>
                  <p className="text-sm text-white leading-relaxed">{brief.brief?.pattern}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#E89B6E] mb-1">Making sense of it</p>
                  <p className="text-sm text-white/90 leading-relaxed">{brief.brief?.insight}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#E89B6E] mb-2">Focus area · {brief.brief?.focus_area}</p>
                  <ul className="space-y-2">
                    {(brief.brief?.drills || []).map((d, i) => (
                      <li key={i} className="bg-white/5 border-l-2 border-[#E89B6E] p-3">
                        <p className="text-sm text-white font-semibold">{d.title}</p>
                        <p className="text-xs text-white/80 leading-relaxed mt-1">{d.how}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScoreVsMentalPanel;
